<template>
	<div>
		<CloMapGoogleMarker
			v-for="(marker, index) in markers"
			:key="index"
			:marker="marker"
			:content="marker.content"
			:google="google"
			:map="map"
			v-on:marker-click="markerClick"
		/>
	</div>
</template>
<script>
import CloMapGoogleMarker from '@/maps/CloMapGoogle/CloMapGoogleMarker.vue';
export default {
	name: 'CloMapGoogleObservations',
	components: {
		CloMapGoogleMarker,
	},
	props: {
		google: {
			type: Object,
			required: true,
		},
		map: {
			type: Object,
			required: true,
		},
		gridLarge: {
			type: String,
			default: null,
		},
		gridSmall: {
			type: String,
			default: null,
		},
		gridNegativeLarge: {
			type: String,
			default: null,
		},
		gridNegativeSmall: {
			type: String,
			default: null,
		},
		showNegativeGrid: {
			type: Boolean,
			default: false,
		},
		showPoints: {
			type: Boolean,
			default: false,
		},
		gridSizeZoomThreshold: {
			type: Number,
			default: 6,
		},
		pointsZoomThreshold: {
			type: Number,
			default: 10,
		},
		points: {
			type: Array,
			default: null,
		},
		dataBounds: {
			type: Object,
			default: null,
		},
		geoCacheUrl: {
			type: String,
			required: true,
		},
		infoWindow: {
			type: Object,
			default: null,
		},
		infoWindowZIndex: {
			type: Number,
			default: 1,
		},
	},
	data() {
		return {
			currentGridSize: null,
			markers: [],
		};
	},
	watch: {
		showNegativeGrid(newValue) {
			this.toggleNegativeGrid();
		},
		infoWindow: {
			deep: true,
			handler() {
				this.markers[this.infoWindow.index].content = this.infoWindow.content;
			},
		},
		dataBounds() {
			const bounds = new this.google.maps.LatLngBounds(
				new this.google.maps.LatLng(this.dataBounds.minY, this.dataBounds.minX),
				new this.google.maps.LatLng(this.dataBounds.maxY, this.dataBounds.maxX)
			);
			this.map.fitBounds(bounds);
			this.refreshLayers();
		},
		layers() {
			this.refreshLayers(true);
		},
	},
	computed: {
		layers() {
			return [this.gridLarge, this.gridSmall, this.gridNegativeLarge, this.gridNegativeSmall]
		}
	},
	// TODO:
	// optionally show markers at certain zoom level
	// handle change of zoom level when markers are shown
	// handle waiting for RSIDs
	// draw box to set zoom/bounds
	// show media markers
	// add tiles loaded event?
	mounted() {
		// add delay to avoid undefined this.map error
		setTimeout(() => {
			this.initializeMap();
		}, 10)
	},
	methods: {
		initializeMap() {
			// set bounds to dataBounds
			const bounds = new this.google.maps.LatLngBounds(
				new this.google.maps.LatLng(this.dataBounds.minY, this.dataBounds.minX),
				new this.google.maps.LatLng(this.dataBounds.maxY, this.dataBounds.maxX)
			);
			this.map.fitBounds(bounds);
			
			// set layers to null (is this really needed?)
			this.map.overlayMapTypes.push(null); // large
			this.map.overlayMapTypes.push(null); // small
			this.map.overlayMapTypes.push(null); // large negative
			this.map.overlayMapTypes.push(null); // small negative

			this.refreshLayers();

			this.google.maps.event.addListener(this.map, 'zoom_changed', this.refreshLayers);

			this.google.maps.event.addListener(this.map, 'dragend', this.refreshLayers);
		},
		refreshLayers(forceRefresh = false) {
			const zoom = this.map.getZoom();
			const gridSize = zoom < this.gridSizeZoomThreshold ? 'large' : zoom <= this.pointsZoomThreshold ? 'small' : 'points';
			//console.log('grid', gridSize, 'zoom', zoom, 'layers', this.layers);
			if (gridSize !== this.currentGridSize || forceRefresh) {
				this.currentGridSize = gridSize;
				if (gridSize !== 'points') {
					this.hideMarkers();
					const positiveLayerIndexShow = gridSize === 'large' ? 0 : 1;
					const positiveLayerIndexHide = gridSize === 'large' ? 1 : 0;
					// positive data
					//if (this.map.overlayMapTypes.getAt(positiveLayerIndexShow) === null) {
						this.showLayer(positiveLayerIndexShow);
					//}
					
					this.map.overlayMapTypes.setAt(positiveLayerIndexHide, null);
					// negative data
					if (this.showNegativeGrid) {
						const negativeLayerIndexShow = gridSize === 'large' ? 2 : 3;
						const negativeLayerIndexHide = gridSize === 'large' ? 3 : 2;
						//if (this.map.overlayMapTypes.getAt(negativeLayerIndexShow) === null) {
							this.showLayer(negativeLayerIndexShow);
						//}
						this.map.overlayMapTypes.setAt(negativeLayerIndexHide, null);
					} else {
						// hide all negative data
						this.map.overlayMapTypes.setAt(2, null);
						this.map.overlayMapTypes.setAt(3, null);
					}
				} else {
					//this.showMarkers();
					this.map.overlayMapTypes.setAt(0, null);
					this.map.overlayMapTypes.setAt(1, null);
					this.map.overlayMapTypes.setAt(2, null);
					this.map.overlayMapTypes.setAt(3, null);
				}
			}
		},
		toggleNegativeGrid() {
			if (this.currentGridSize !== 'points') {
				if (this.showNegativeGrid) {
					const negativeLayerIndexShow = this.currentGridSize === 'large' ? 2 : 3;
					const negativeLayerIndexHide = this.currentGridSize === 'large' ? 3 : 2;
					if (this.map.overlayMapTypes.getAt(negativeLayerIndexShow) === null) {
						this.showLayer(negativeLayerIndexShow);
					}
					this.map.overlayMapTypes.setAt(negativeLayerIndexHide, null);
				} else {
					// hide all negative data
					this.map.overlayMapTypes.setAt(2, null);
					this.map.overlayMapTypes.setAt(3, null);
				}
			}
		},
		showLayer(layerIndex) {
			//console.log('showLayer', layerIndex);
			const gridOptions = {
				getTileUrl: (coord, zoom) => {
					const normalizedPoint = this.getNormalizedPoint(coord, zoom);
					if (!normalizedPoint) {
						return null;
					}
					return (
						this.geoCacheUrl +
						'&zoom=' +
						zoom +
						'&x=' +
						normalizedPoint.x +
						'&y=' +
						normalizedPoint.y +
						'&CQL_FILTER=' +
						"result_set_id='" +
						this.layers[layerIndex] +
						"'"
					);
				},
				tileSize: new this.google.maps.Size(256, 256),
				isPng: true,
				//opacity: 1.0, //opacity || 1.0
			};
			const gridMapType = new this.google.maps.ImageMapType(gridOptions);

			this.map.overlayMapTypes.setAt(layerIndex, gridMapType);
		},
		// https://developers.google.com/maps/documentation/javascript/examples/maptype-image
		// normalize pointinates so tiles repeat along the X axis
		getNormalizedPoint(point, zoom) {
			let y = point.y;
			let x = point.x;
			// tile range in one direction range is dependent on zoom level
			// 0 = 1 tile, 1 = 2 tiles, 2 = 4 tiles, 3 = 8 tiles, etc
			const tileRange = 1 << zoom;
			// don't repeat across y-axis
			if (y < 0 || y >= tileRange) {
				return null;
			}
			// repeat across x-axis
			if (x < 0 || x >= tileRange) {
				x = ((x % tileRange) + tileRange) % tileRange;
			}
			return { x: x, y: y };
		},
		showMarkers() {
			this.markers = this.points.map((point) => {
				return {
					position: new this.google.maps.LatLng(point.y, point.x),
					type: point.hs ? 'hotspot' : 'personal',
					size: point.rec ? 'lg' : 'sm',
					content: point.content,
					id: point.n,
				};
			});
		},
		hideMarkers() {
			this.markers = [];
		},
		// pass event up from marker to parent
		markerClick(marker) {
			this.$emit('marker-click', marker);
		},
	},
};
</script>

<style></style>
